import { useState, useRef } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2, User, Mail, Lock, Globe, Phone, Calendar, Sparkles, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useVoice, useVoiceGuide } from "@/contexts/VoiceAssistantContext";
import { COUNTRY_CODES, formatPhoneNumber } from "@/data/countryCodes";

const COUNTRIES = [
  "México","Estados Unidos","España","Argentina","Colombia","Chile","Perú","Venezuela",
  "Ecuador","Bolivia","Paraguay","Uruguay","Guatemala","Honduras","El Salvador","Nicaragua",
  "Costa Rica","Panamá","Rep. Dominicana","Cuba","Puerto Rico","Brasil","Canadá","Otro",
];

const EMAIL_DOMAINS = [
  "gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "icloud.com",
  "live.com", "msn.com", "protonmail.com", "yahoo.com.mx", "hotmail.es",
];

// Build live email-autocomplete suggestions as the user types. Returns up
// to 5 full email strings ("local@gmail.com", ...) — works on every browser
// because we use a plain dropdown (not <datalist>, which Safari renders
// inconsistently on iOS).
const emailSuggestions = (val) => {
  if (!val) return [];
  const at = val.indexOf("@");
  if (at < 0) {
    return EMAIL_DOMAINS.slice(0, 5).map((d) => `${val}@${d}`);
  }
  const local = val.slice(0, at);
  if (!local) return [];
  const partial = val.slice(at + 1).toLowerCase();
  const matches = EMAIL_DOMAINS.filter((d) => d.startsWith(partial));
  if (matches.length === 0) return [];
  return matches.slice(0, 5).map((d) => `${local}@${d}`);
};

const formatErr = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e?.msg).join(" ") : "Error");

export default function UserAuthPage({ mode = "login" }) {
  const { user, loading, login, logout, register, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get("next") || "";
  const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "";
  const [tab, setTab] = useState(mode);
  const justRegistered = useRef(false);
  const voice = useVoice();
  useVoiceGuide(`auth:${tab}`, tab === "register"
    ? "Vamos a crear tu cuenta, es gratis y toma un minuto. Escribe tu nombre completo, un nombre de usuario, tu correo, una contraseña de al menos ocho caracteres y repítela. Después tu fecha de nacimiento, país y WhatsApp. Cuando termines, toca Crear cuenta. Si algo falta, te lo diré."
    : "Ingresa con tu usuario o correo y tu contraseña. Si aún no tienes cuenta, toca Crear cuenta y te guío paso a paso.", !user);
  const [submitting, setSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loginId, setLoginId] = useState("");
  const [loginPw, setLoginPw] = useState("");
  const [form, setForm] = useState({
    username: "", email: "", password: "", password2: "", name: "", dob: "", age: "",
    country: "", phone_country: "MX", phone_dial: "+52", phone_digits: "", phone: "",
    terms_accepted: true,   // per business rules: always ON, checkbox is a display marker only.
  });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [emailHints, setEmailHints] = useState([]);
  const [emailHintsOpen, setEmailHintsOpen] = useState(false);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const USERNAME_RE = /^[a-zA-Z0-9._-]+$/; // letras, números, . _ - (no espacios)
  // Full-name rule: 1-2 nombres + 2 apellidos → 3 o 4 palabras alfabéticas
  // (letras acentuadas + ñ + apóstrofos aceptados; guiones entre palabras
  // se toleran para nombres compuestos como "Ana-María"). Cada palabra
  // debe iniciar con mayúscula (o serse permisiva y validar por tokens).
  const NAME_TOKEN_RE = /^[A-ZÁÉÍÓÚÑ][a-záéíóúñü'’-]+(?:[- ][A-ZÁÉÍÓÚÑ][a-záéíóúñü'’-]+)?$/;
  const validateFullName = (raw) => {
    const parts = String(raw || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length < 3) return "Debe contener 1-2 nombres y 2 apellidos (mínimo 3 palabras)";
    if (parts.length > 4) return "Máximo 2 nombres y 2 apellidos (4 palabras)";
    // Every token must look like a name.
    for (const p of parts) {
      if (!/^[A-Za-zÁÉÍÓÚÑÜáéíóúñü'’-]{2,}$/.test(p)) {
        return "Solo letras (sin números ni símbolos)";
      }
    }
    return null;
  };

  // Compute age from a "YYYY-MM-DD" DOB string; returns "" if invalid.
  const computeAge = (dob) => {
    if (!dob) return "";
    const parts = dob.split("-");
    if (parts.length !== 3) return "";
    const [yyyy, mm, dd] = parts.map((x) => parseInt(x, 10));
    if (!yyyy || !mm || !dd) return "";
    const today = new Date();
    let age = today.getFullYear() - yyyy;
    const monthDiff = today.getMonth() + 1 - mm;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dd)) age--;
    return age >= 0 && age <= 120 ? String(age) : "";
  };

  const validateRegister = () => {
    const errs = {};
    if (!form.username.trim()) errs.username = "Requerido";
    else if (/\s/.test(form.username)) errs.username = "El usuario no puede contener espacios";
    else if (!USERNAME_RE.test(form.username)) errs.username = "Solo letras, números, punto, guion o guion bajo";
    else if (form.username.length < 3) errs.username = "Mínimo 3 caracteres";
    if (!EMAIL_RE.test(form.email.trim())) errs.email = "Email inválido (ej. nombre@dominio.com)";
    if (!form.password || form.password.length < 6) errs.password = "Mínimo 6 caracteres";
    if (form.password !== form.password2) errs.password2 = "Las contraseñas no coinciden";
    const nameErr = validateFullName(form.name);
    if (nameErr) errs.name = nameErr;
    if (!form.dob) errs.dob = "Fecha de nacimiento requerida";
    else if (!computeAge(form.dob)) errs.dob = "Fecha inválida";
    else if (Number(computeAge(form.dob)) < 18) errs.dob = "Debes ser mayor de 18 años";
    if (!form.country) errs.country = "Selecciona país";
    if (!form.phone_digits.trim() || form.phone_digits.replace(/\D/g, "").length < 6) errs.phone = "Teléfono inválido (mínimo 6 dígitos)";
    // terms_accepted is always true (locked); no validation needed.
    setErrors(errs);
    const errList = Object.values(errs);
    if (errList.length) voice.speak(`Revisa estos datos: ${errList.slice(0, 3).join(". ")}.`, { once: false });
    return Object.keys(errs).length === 0;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" /></div>;
  }
  if (user) return <Navigate to={isAdmin ? "/admin" : (justRegistered.current ? "/servicios" : (nextPath || "/mi-cuenta"))} replace />;

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const u = await login(loginId.trim(), loginPw, rememberMe);
      if (u.role === "admin") {
        await logout();
        toast.error("Esta cuenta no se inicia aquí.");
        return;
      }
      toast.success("Bienvenido " + (u.name || u.username));
      navigate(nextPath || "/mi-cuenta");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Credenciales inválidas");
    } finally { setSubmitting(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateRegister()) {
      toast.error("Revisa los campos marcados en rojo");
      return;
    }
    setSubmitting(true);
    // NOTE: Geolocation is intentionally NOT requested during registration.
    // Per business rules, ubicación se captura sólo al agendar / en movimientos
    // posteriores. Keeps the sign-up flow frictionless.
    const phoneFull = `${form.phone_dial} ${formatPhoneNumber(form.phone_digits)}`.trim();
    const { password2: _ignored, phone_dial: _d, phone_digits: _g, phone_country: _pc, dob, ...rest } = form;
    const age = Number(computeAge(dob));
    const payload = { ...rest, phone: phoneFull, terms_accepted: true, dob, age };
    try {
      const u = await register(payload);
      justRegistered.current = true;
      toast.success("¡Cuenta creada! Bienvenido " + u.name);
      voice.speak(`¡Bienvenido ${(u.name || "").split(" ")[0]}! Tu cuenta ya está lista. Ahora puedes ver los servicios con sus precios, hablar con Cristopher en el chat dorado y registrar tu pago cuando estés listo. Si tienes una duda, toca el micrófono y pregúntame.`, { once: false, sticky: true });
      navigate("/servicios", { replace: true });
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || "Error al registrar");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen pt-20 pb-8 px-3 sm:pt-28 sm:pb-12 sm:px-6 flex items-start sm:items-center justify-center" data-testid="auth-page">
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-[#1a0000]/30 pointer-events-none" />
      <div className="relative w-full max-w-xl">
        <div className="text-center mb-4 sm:mb-6">
          <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-[#d4af37] mx-auto mb-2" />
          <h1 className="font-serif text-2xl sm:text-3xl text-white">{tab === "login" ? "Ingresar" : "Crear Cuenta"}</h1>
          <p className="text-xs sm:text-sm text-neutral-400 mt-1">
            {tab === "login" ? "Accede a tu cuenta de CrisAngel Astral" : "Únete y desbloquea consultas, citas y mensajes privados con CrisAngel Astral"}
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 bg-black/40 border border-white/10 rounded-full p-1 mb-6">
          <button data-testid="tab-login" onClick={() => setTab("login")} className={`py-2.5 text-xs uppercase tracking-widest rounded-full transition-all ${tab === "login" ? "bg-[#d4af37] text-black" : "text-neutral-400"}`}>Iniciar Sesión</button>
          <button data-testid="tab-register" onClick={() => setTab("register")} className={`py-2.5 text-xs uppercase tracking-widest rounded-full transition-all ${tab === "register" ? "bg-[#d4af37] text-black" : "text-neutral-400"}`}>Registrarse</button>
        </div>

        <div className="glass-dark border border-[#d4af37]/30 rounded-2xl p-4 sm:p-7">
          {tab === "login" ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-neutral-300 mb-1 flex items-center gap-2 text-xs uppercase tracking-widest"><User className="w-3 h-3 text-[#d4af37]" /> Email o usuario</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Escribe el email o el nombre de usuario con el que te registraste.</p>
                <Input data-testid="login-id" required value={loginId} onChange={(e) => setLoginId(e.target.value)} className="bg-black/40 border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1 flex items-center gap-2 text-xs uppercase tracking-widest"><Lock className="w-3 h-3 text-[#d4af37]" /> Contraseña</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">La contraseña que elegiste al crear tu cuenta. Distingue entre mayúsculas y minúsculas.</p>
                <Input data-testid="login-pw" type="password" required value={loginPw} onChange={(e) => setLoginPw(e.target.value)} className="bg-black/40 border-white/10 text-white" />
              </div>
              <label className="flex items-start gap-2 cursor-pointer select-none" data-testid="login-remember-wrap">
                <input type="checkbox" data-testid="login-remember" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="mt-0.5 accent-[#d4af37]" />
                <span className="text-[11px] text-neutral-300 leading-snug">Mantener mi sesión abierta en este dispositivo<br/><span className="text-neutral-500">Reconoceremos este navegador y no te pediremos la contraseña de nuevo hasta que cierres sesión.</span></span>
              </label>
              <button data-testid="login-submit" type="submit" disabled={submitting} className="btn-gold w-full py-3.5 rounded-full uppercase text-xs tracking-widest disabled:opacity-60">{submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Entrar"}</button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="space-y-3" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Usuario</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Nombre único con el que iniciarás sesión. Sin espacios, mínimo 3 caracteres. Sé breve y fácil de recordar.</p>
                  <Input
                    data-testid="reg-username"
                    required
                    minLength={3}
                    value={form.username}
                    onChange={(e) => {
                      // strip spaces in real-time
                      const v = e.target.value.replace(/\s+/g, "");
                      setForm({ ...form, username: v });
                      if (errors.username) setErrors({ ...errors, username: undefined });
                    }}
                    onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                    placeholder="sin espacios"
                    className={`bg-black/40 text-white ${errors.username ? "border-red-500" : "border-white/10"}`}
                  />
                  {errors.username && <p data-testid="err-username" className="text-[10px] text-red-400 mt-1">{errors.username}</p>}
                </div>
                <div className="relative">
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Email</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Tu correo activo. Ahí llegarán confirmaciones y (si lo llegas a perder) el enlace para recuperar tu contraseña.</p>
                  <Input
                    data-testid="reg-email"
                    type="email"
                    required
                    autoComplete="email"
                    value={form.email}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm({ ...form, email: v });
                      if (errors.email) setErrors({ ...errors, email: undefined });
                      const hints = emailSuggestions(v.trim());
                      setEmailHints(hints);
                      setEmailHintsOpen(hints.length > 0);
                    }}
                    onFocus={() => {
                      const hints = emailSuggestions(form.email.trim());
                      setEmailHints(hints);
                      setEmailHintsOpen(hints.length > 0);
                    }}
                    onBlur={(e) => {
                      // Delay closing so the click on a suggestion fires first
                      setTimeout(() => setEmailHintsOpen(false), 160);
                      const v = e.target.value.trim();
                      if (v && !EMAIL_RE.test(v)) setErrors((p) => ({ ...p, email: "Email inválido (ej. nombre@dominio.com)" }));
                    }}
                    placeholder="nombre@dominio.com"
                    className={`bg-black/40 text-white ${errors.email ? "border-red-500" : "border-white/10"}`}
                  />
                  {emailHintsOpen && emailHints.length > 0 && (
                    <ul
                      data-testid="email-suggestions"
                      className="absolute z-30 left-0 right-0 mt-1 rounded-lg border border-[#d4af37]/30 bg-[#0a0a0a] shadow-xl overflow-hidden"
                    >
                      {emailHints.map((h) => (
                        <li key={h}>
                          <button
                            type="button"
                            data-testid={`email-hint-${h}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setForm((f) => ({ ...f, email: h }));
                              setEmailHintsOpen(false);
                              if (errors.email) setErrors({ ...errors, email: undefined });
                            }}
                            className="w-full text-left px-3 py-1.5 text-sm text-neutral-200 hover:bg-[#d4af37]/15 hover:text-[#d4af37]"
                          >
                            {h}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {errors.email && <p data-testid="err-email" className="text-[10px] text-red-400 mt-1">{errors.email}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Contraseña (mín. 6)</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Elige una contraseña segura: mezcla mayúsculas, minúsculas y números. No la compartas con nadie.</p>
                  <div className="relative">
                    <Input
                      data-testid="reg-password"
                      type={showPw ? "text" : "password"}
                      minLength={6}
                      required
                      autoComplete="new-password"
                      value={form.password}
                      onChange={(e) => {
                        setForm({ ...form, password: e.target.value });
                        if (errors.password) setErrors({ ...errors, password: undefined });
                      }}
                      className={`bg-black/40 text-white pr-10 ${errors.password ? "border-red-500" : "border-white/10"}`}
                    />
                    <button
                      type="button"
                      data-testid="reg-password-toggle"
                      onClick={() => setShowPw((v) => !v)}
                      className="absolute inset-y-0 right-2 my-auto h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-[#d4af37]"
                      aria-label={showPw ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {errors.password && <p data-testid="err-password" className="text-[10px] text-red-400 mt-1">{errors.password}</p>}
                </div>
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Confirmar contraseña</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Escribe otra vez la misma contraseña para asegurarnos que no hay errores.</p>
                  <div className="relative">
                    <Input
                      data-testid="reg-password2"
                      type={showPw2 ? "text" : "password"}
                      minLength={6}
                      required
                      autoComplete="new-password"
                      value={form.password2}
                      onChange={(e) => {
                        const v = e.target.value;
                        setForm({ ...form, password2: v });
                        // Live mismatch validation
                        if (form.password && v && v !== form.password) {
                          setErrors((p) => ({ ...p, password2: "Las contraseñas no coinciden" }));
                        } else if (errors.password2) {
                          setErrors({ ...errors, password2: undefined });
                        }
                      }}
                      onBlur={(e) => {
                        if (form.password && e.target.value && e.target.value !== form.password) {
                          setErrors((p) => ({ ...p, password2: "Las contraseñas no coinciden" }));
                        }
                      }}
                      className={`bg-black/40 text-white pr-10 ${errors.password2 ? "border-red-500" : "border-white/10"}`}
                    />
                    <button
                      type="button"
                      data-testid="reg-password2-toggle"
                      onClick={() => setShowPw2((v) => !v)}
                      className="absolute inset-y-0 right-2 my-auto h-7 w-7 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-[#d4af37]"
                      aria-label={showPw2 ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showPw2 ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {form.password && form.password2 && form.password === form.password2 && !errors.password2 && (
                    <p data-testid="ok-password2" className="text-[10px] text-emerald-400 mt-1">✓ Las contraseñas coinciden</p>
                  )}
                  {errors.password2 && <p data-testid="err-password2" className="text-[10px] text-red-400 mt-1">{errors.password2}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Nombre completo</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Escribe 1 o 2 nombres y tus 2 apellidos (ej. &quot;María Guadalupe López Ramírez&quot;). Sin números ni abreviaturas — se usa para identificarte en cada trabajo espiritual.</p>
                  <Input
                    data-testid="reg-name"
                    required
                    value={form.name}
                    placeholder="Ej. María López Ramírez"
                    onChange={(e) => {
                      const v = e.target.value.replace(/[0-9]/g, "");
                      setForm({ ...form, name: v });
                      if (errors.name) setErrors({ ...errors, name: undefined });
                    }}
                    className={`bg-black/40 text-white ${errors.name ? "border-red-500" : "border-white/10"}`}
                  />
                  {errors.name && <p data-testid="err-name" className="text-[10px] text-red-400 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Fecha de nacimiento</Label>
                  <p className="text-[10px] text-neutral-500 mb-1.5">Selecciona el calendario o escribe DD/MM/AAAA. Tu edad se calcula automáticamente.</p>
                  <Input
                    data-testid="reg-dob"
                    required
                    type="date"
                    max={new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)}
                    min="1900-01-01"
                    value={form.dob}
                    onChange={(e) => {
                      const dob = e.target.value;
                      setForm({ ...form, dob, age: computeAge(dob) });
                      if (errors.dob) setErrors({ ...errors, dob: undefined });
                    }}
                    className={`bg-black/40 text-white ${errors.dob ? "border-red-500" : "border-white/10"}`}
                  />
                  {errors.dob && <p data-testid="err-dob" className="text-[10px] text-red-400 mt-1">{errors.dob}</p>}
                </div>
              </div>

              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Edad (calculada)</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Este campo se llena automáticamente con base en tu fecha de nacimiento y no puede modificarse.</p>
                <Input
                  data-testid="reg-age"
                  type="text"
                  readOnly
                  disabled
                  value={form.age ? `${form.age} años` : ""}
                  placeholder="Se calculará al elegir tu fecha de nacimiento"
                  className="bg-black/60 border-white/10 text-neutral-400 cursor-not-allowed"
                />
              </div>
              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">País</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Nos ayuda a mostrarte precios en tu moneda y opciones de pago que aplican en tu país.</p>
                <Select value={form.country} onValueChange={(v) => setForm({...form, country: v})}>
                  <SelectTrigger data-testid="reg-country" className="bg-black/40 border-white/10 text-white"><SelectValue placeholder="Selecciona tu país" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white max-h-72">
                    {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-neutral-300 mb-1 text-xs uppercase tracking-widest">Teléfono / WhatsApp</Label>
                <p className="text-[10px] text-neutral-500 mb-1.5">Selecciona el código de tu país y escribe tu número (solo números). Es el mismo que usas en WhatsApp para poder contactarte.</p>
                <div className="grid grid-cols-[6.5rem_1fr] gap-2">
                  <Select value={form.phone_country} onValueChange={(code) => {
                    const c = COUNTRY_CODES.find((x) => x.code === code);
                    setForm({ ...form, phone_country: code, phone_dial: c ? c.dial : form.phone_dial });
                  }}>
                    <SelectTrigger data-testid="reg-dial" className="bg-black/40 border-white/10 text-white">
                      <SelectValue placeholder="+52" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white max-h-72">
                      {COUNTRY_CODES.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.dial} — {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    data-testid="reg-phone"
                    required
                    inputMode="tel"
                    placeholder="Solo números"
                    value={formatPhoneNumber(form.phone_digits)}
                    onChange={(e) => {
                      // strip non-digits, max 15 digits per E.164
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 15);
                      setForm({ ...form, phone_digits: digits });
                      if (errors.phone) setErrors({ ...errors, phone: undefined });
                    }}
                    onKeyDown={(e) => {
                      const allowed = ["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Home","End"];
                      if (allowed.includes(e.key)) return;
                      if (e.metaKey || e.ctrlKey) return;
                      if (!/^\d$/.test(e.key)) e.preventDefault();
                    }}
                    className={`bg-black/40 text-white ${errors.phone ? "border-red-500" : "border-white/10"}`}
                  />
                </div>
                {errors.phone && <p data-testid="err-phone" className="text-[10px] text-red-400 mt-1">{errors.phone}</p>}
              </div>
              <div className="pt-1">
                <label className="flex items-start gap-2.5 cursor-not-allowed group">
                  <input
                    data-testid="reg-terms"
                    type="checkbox"
                    checked
                    disabled
                    readOnly
                    aria-label="Términos y condiciones (aceptados automáticamente)"
                    className="mt-0.5 w-4 h-4 accent-[#d4af37] cursor-not-allowed flex-shrink-0"
                  />
                  <span className="text-[11px] sm:text-xs text-neutral-300 leading-snug">
                    Al continuar aceptas los{" "}
                    <button
                      type="button"
                      data-testid="link-terms"
                      onClick={() => toast.info("Los Términos y Condiciones aún no están disponibles.")}
                      className="text-[#d4af37] underline hover:text-[#f1c869]"
                    >
                      Términos y Condiciones
                    </button>.
                  </span>
                </label>
              </div>
              <button data-testid="reg-submit" type="submit" disabled={submitting} className="btn-gold w-full py-3.5 rounded-full uppercase text-xs tracking-widest mt-2 disabled:opacity-60 disabled:cursor-not-allowed">{submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : "Crear Cuenta"}</button>
            </form>
          )}

          <p className="text-center text-xs text-neutral-500 mt-6">
            {tab === "login" ? (
              <>¿No tienes cuenta? <button onClick={() => setTab("register")} className="text-[#d4af37] hover:underline">Regístrate</button></>
            ) : (
              <>¿Ya tienes cuenta? <button onClick={() => setTab("login")} className="text-[#d4af37] hover:underline">Inicia sesión</button></>
            )}
          </p>
        </div>

        <p className="text-center text-xs text-neutral-500 mt-6">
          <Link to="/" className="hover:text-[#d4af37]">← Volver al inicio</Link>
        </p>
      </div>
    </div>
  );
}
